import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Badge, Button, Card, Stack, Typography } from "@altimateai/lego";
import { IconArrowLeft } from "@tabler/icons-react";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { FilterType, TableHeaderType } from "@/components/tables/types";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { getTasksRuns, getTasksRunsGraphData, getTasksRunsSummary } from "@/helpers/apis";
import { TASK_RUN_STATES, TaskRunResponse } from "@/pages/query/types";
import { formatMilliseconds } from "@/helpers/utils";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { Graph } from "@/pages/query/Graph";
import { TasksRunsSummary } from "./TasksRunsSummary";

const SORT_SEARCH_KEY = "sortAttribute";
const SORT_ORDER_SEARCH_KEY = "sortOrder";

// Maps each canonical Snowflake task run state to a Badge variant.
// SUCCEEDED                  → success
// FAILED / FAILED_AND_AUTO_SUSPENDED / CANCELLED → destructive (terminal-bad)
// SCHEDULED / EXECUTING / SKIPPED                → secondary   (in-flight or no-op)
// Unknown values also fall back to secondary so a backend state we don't
// recognize still renders instead of throwing.
const stateBadgeVariant = (state: string): "success" | "destructive" | "secondary" => {
  const normalized = state.toUpperCase();
  if (normalized === "SUCCEEDED") {
    return "success";
  }
  if (
    normalized === "FAILED" ||
    normalized === "FAILED_AND_AUTO_SUSPENDED" ||
    normalized === "CANCELLED"
  ) {
    return "destructive";
  }
  return "secondary";
};

const formatDuration = (ms: number | null): string => (ms === null ? "—" : formatMilliseconds(ms));

export const TasksRunHistory = () => {
  const { database, schema, name } = useParams();
  const {
    page,
    pageSize,
    setPage,
    setPageSize,
    totalItems,
    totalPages,
    setTotalItems,
    setTotalPages,
  } = useTablePagination();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const sortAttribute = searchParams.get("sortAttribute") ?? "scheduled_time";
  const sortOrder = searchParams.get("sortOrder") ?? "desc";
  // Multi-select state filter is URL-driven so it survives reloads / share-links
  // and stays consistent with the parent Tasks list page's filter convention.
  const states = useMemo(() => searchParams.getAll("states"), [searchParams]);

  useBreadcrumb(name ?? "Task Runs", {
    defaultParents: [
      { label: "Code", pathname: "/query" },
      { label: "Tasks", pathname: "/query/tasks" },
    ],
  });

  const taskIdentityEnabled = Boolean(database && schema && name);

  // Aggregate stats + per-day cost graph are scoped to the task identity only
  // (no paging / sort) — mirrors the stored-procedures run-history layout.
  const { data: summaryData, isLoading: isSummaryLoading } = useQuery({
    queryKey: ["tasks_runs_summary", database, schema, name],
    queryFn: () =>
      getTasksRunsSummary({
        database_name: database!,
        schema_name: schema!,
        name: name!,
      }),
    enabled: taskIdentityEnabled,
  });

  const { data: graphData, isLoading: isGraphLoading } = useQuery({
    queryKey: ["tasks_runs_graph_data", database, schema, name],
    queryFn: () =>
      getTasksRunsGraphData({
        database_name: database!,
        schema_name: schema!,
        name: name!,
      }),
    enabled: taskIdentityEnabled,
  });

  // The Graph component expects { timestamp, query_cost, execution_time };
  // map the tasks runs_graph_data shape ({ date, cost, execution_time_ms }).
  const graphPoints = useMemo(
    () =>
      (graphData?.data ?? []).map(point => ({
        timestamp: point.date,
        query_cost: point.cost,
        execution_time: point.execution_time_ms,
      })),
    [graphData]
  );

  const { data, isLoading } = useQuery({
    queryKey: [
      "tasks_runs",
      database,
      schema,
      name,
      page,
      pageSize,
      sortAttribute,
      sortOrder,
      states,
    ],
    queryFn: () =>
      getTasksRuns({
        database_name: database!,
        schema_name: schema!,
        name: name!,
        page: page + 1,
        size: pageSize,
        sortAttribute,
        sortOrder,
        // Only attach the param when at least one state is selected — keeps
        // the no-filter URL clean (`states` absent rather than `states=`).
        ...(states.length > 0 ? { states } : {}),
      }),
    enabled: taskIdentityEnabled,
  });

  // Sync API response totals into the datatable pagination context
  // (replaces onSuccess callback removed in TanStack Query v5).
  useEffect(() => {
    if (data) {
      setTotalItems(data?.total ?? 0);
      setTotalPages(data?.pages ?? 0);
    }
  }, [data, setTotalItems, setTotalPages]);

  const tableHeaders: TableHeaderType<TaskRunResponse>[] = [
    {
      id: 1,
      label: "Query ID",
      uid: "query_id",
      width: "25%",
      render: (item: TaskRunResponse) => (
        <span className="al-text-brand condense-text cursor-pointer">{item.query_id}</span>
      ),
    },
    {
      id: 2,
      label: "State",
      uid: "state",
      render: (item: TaskRunResponse) => (
        <Badge variant={stateBadgeVariant(item.state)}>{item.state}</Badge>
      ),
      // Server-side filter: sent to /query/v2/tasks/runs as `?states=...`.
      // A client-only filter would only filter the current page (server
      // paginates), so multi-page filtering would silently break.
      filter: {
        id: "states",
        label: "State",
        searchKey: "states",
        filterType: FilterType.DROPDOWN,
        value: states,
        options: TASK_RUN_STATES.map(s => ({ label: s, value: s })),
        onChange: (value: string[]) => {
          // Reset to first page so a narrower result set doesn't strand the
          // user on a now-out-of-range page number.
          updateSearchParamsByKey({ states: value, page: "0" });
        },
      },
    },
    {
      id: 3,
      label: "Scheduled Time",
      uid: "scheduled_time",
      render: (item: TaskRunResponse) => (
        <div>{dayjs(item.scheduled_time).format("YYYY-MM-DD hh:mm a")}</div>
      ),
      sort: {
        sortSearchKey: SORT_SEARCH_KEY,
        sortOrderSearchKey: SORT_ORDER_SEARCH_KEY,
        sortParamValue: "scheduled_time",
      },
    },
    {
      id: 4,
      label: "Queued Time",
      uid: "queued_time_ms",
      render: (item: TaskRunResponse) => <div>{formatDuration(item.queued_time_ms)}</div>,
      sort: {
        sortSearchKey: SORT_SEARCH_KEY,
        sortOrderSearchKey: SORT_ORDER_SEARCH_KEY,
        sortParamValue: "queued_time_ms",
      },
    },
    {
      id: 5,
      label: "Execution Time",
      uid: "execution_time_ms",
      render: (item: TaskRunResponse) => <div>{formatDuration(item.execution_time_ms)}</div>,
      sort: {
        sortSearchKey: SORT_SEARCH_KEY,
        sortOrderSearchKey: SORT_ORDER_SEARCH_KEY,
        sortParamValue: "execution_time_ms",
      },
    },
    {
      id: 6,
      label: "Attempt",
      uid: "attempt_number",
      width: "8%",
      render: (item: TaskRunResponse) => <div>{item.attempt_number}</div>,
    },
  ];

  return (
    <Card className="al-p-4">
      <Stack direction="column" gap={"lg"}>
        <Stack align={"center"} gap={"sm"}>
          <Button asChild size={"icon"} variant={"ghost"} title="Go back">
            <Link to={"/query/tasks"} aria-label="Go back">
              <IconArrowLeft size={18} />
            </Link>
          </Button>
          <Typography className={"!al-text-base"} variant={"section-heading"}>
            {name}
          </Typography>
        </Stack>
        {!isSummaryLoading && summaryData && (
          // Database/Schema are rendered inside TasksRunsSummary so the page
          // header stays single-line and all metadata follows one pattern.
          <TasksRunsSummary summaryData={summaryData} database={database} schema={schema} />
        )}
        <Stack direction={"column"} className={"al-p-4 al-border al-rounded-md"}>
          <Typography variant={"section-heading"}>Runs Cost</Typography>
          <Graph isLoading={isGraphLoading} data={graphPoints} />
        </Stack>
        <Stack direction={"column"} gap={"lg"}>
          <Typography variant={"section-heading"}>Runs list</Typography>
          <DataTable
            isLoading={isLoading}
            header={tableHeaders}
            data={{ items: data?.items ?? [] }}
            idKey={(item: TaskRunResponse) => `${item.run_id}-${item.attempt_number}`}
            getRowUrl={(item: TaskRunResponse) => (item.query_id ? `/query/${item.query_id}` : "")}
            defaultSort={{ sortParamValue: "scheduled_time", order: "desc" }}
          />
          <Paginate
            isLoading={isLoading}
            itemCount={totalItems}
            numPages={totalPages}
            page={page}
            pageSize={pageSize}
            onPageClick={setPage}
            onPageSizeChange={setPageSize}
            showPageSize
          />
        </Stack>
      </Stack>
    </Card>
  );
};
